// RUTA: src/lib/supportSla.ts
//
// SLAs (tiempos máximos de respuesta) para tickets de soporte por
// prioridad ITIL. Configurables desde un solo lugar — cuando la
// operación crezca y necesitemos ajustar tiempos, solo cambiamos
// `ACTIVE_SLA = SLA_PRESETS.scaled` aquí y todo el resto de la app
// se actualiza automáticamente (mensajes al cliente, dashboard,
// emails, etc.).
//
// FILOSOFÍA (Joel):
//   - Prometer conservador, cumplir agresivo. Si decimos "1 hora"
//     y respondemos en 30 min, el cliente queda impresionado.
//     Si decimos "30 min" y tardamos 1 hora, perdemos confianza.
//   - Ser realistas — 5 días para una sugerencia es perder al
//     cliente. Mejor 72h con un acuse inmediato.

export type TicketPriority = 'P1' | 'P2' | 'P3' | 'P4';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

interface SlaConfig {
  /** Tiempo máximo en ms para primer respuesta del equipo. */
  firstResponseMs: number;
  /** Etiqueta legible para mostrar al cliente. */
  label: string;
  /** Descripción extendida para emails y mensajes detallados. */
  description: string;
}

/**
 * Configuración ACTUAL para etapa de pocos usuarios (~50, soporte
 * = Joel + Claude). Tiempos cortos y agresivos porque la carga es
 * baja y podemos responder rápido.
 */
const SLA_CURRENT: Record<TicketPriority, SlaConfig> = {
  P1: {
    firstResponseMs: 1 * HOUR,
    label: '1 hora',
    description: 'crítico — respondemos lo antes posible',
  },
  P2: {
    firstResponseMs: 4 * HOUR,
    label: '4 horas',
    description: 'el mismo día (turno de mañana o de noche)',
  },
  P3: {
    firstResponseMs: 24 * HOUR,
    label: '24 horas',
    description: 'dentro del próximo día',
  },
  P4: {
    firstResponseMs: 72 * HOUR,
    label: '3 días',
    description: 'dentro de los próximos 3 días',
  },
};

/**
 * Configuración ESCALADA — la usamos cuando la operación crezca
 * (200-400 usuarios + agentes entrenados). Tiempos más realistas
 * para un equipo más grande con on-call rotation.
 *
 * NO está en uso ahora. Cuando llegue el momento, cambiar
 * `ACTIVE_SLA` arriba a `SLA_SCALED` y deployear. Todo el resto
 * de la app refleja los nuevos tiempos.
 */
// @ts-expect-error — reservado para uso futuro
const SLA_SCALED: Record<TicketPriority, SlaConfig> = {
  P1: { firstResponseMs: 2 * HOUR, label: '2 horas', description: 'crítico' },
  P2: { firstResponseMs: 8 * HOUR, label: '8 horas', description: 'mismo día laboral' },
  P3: { firstResponseMs: 2 * DAY, label: '2 días', description: 'dentro de 2 días laborales' },
  P4: { firstResponseMs: 5 * DAY, label: '5 días', description: 'dentro de 5 días laborales' },
};

/** SLAs activos en la app actualmente. Cambiar cuando escale. */
export const ACTIVE_SLA = SLA_CURRENT;

/**
 * Devuelve la configuración SLA para una prioridad dada.
 */
export function slaForPriority(priority: TicketPriority | null | undefined): SlaConfig {
  return ACTIVE_SLA[priority || 'P3'];
}

/**
 * Calcula la fecha límite de primer respuesta dado un timestamp de
 * creación + prioridad.
 */
export function calculateSlaDueAt(createdAt: Date | string, priority: TicketPriority): Date {
  const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const sla = slaForPriority(priority);
  return new Date(created.getTime() + sla.firstResponseMs);
}

/**
 * ¿Está vencido este SLA? Recibe el due_at del ticket y la fecha
 * de primer respuesta (responded_at). Si responded_at es null,
 * compara contra "ahora".
 */
export function isSlaBreached(slaDueAt: string | Date | null, respondedAt: string | Date | null): boolean {
  if (!slaDueAt) return false;
  const due = typeof slaDueAt === 'string' ? new Date(slaDueAt) : slaDueAt;
  const ref = respondedAt
    ? (typeof respondedAt === 'string' ? new Date(respondedAt) : respondedAt)
    : new Date();
  return ref.getTime() > due.getTime();
}

/**
 * Tiempo restante hasta vencimiento, formateado para humanos.
 * Incluye el prefijo "vence en" — útil para el panel admin donde
 * tiene sentido leer "este ticket vence en 2h".
 *
 *   "vence en 23 min"
 *   "vence en 2h 15min"
 *   "vence mañana"
 *   "vencido"
 */
export function formatTimeRemaining(slaDueAt: string | Date): string {
  const ms = msUntilDue(slaDueAt);
  if (ms <= 0) return 'vencido';
  return `vence en ${formatRemainingDuration(ms)}`;
}

/**
 * Tiempo restante SIN prefijo — útil para componer en frases del lado
 * del cliente ("Te respondemos en 23h 42min"). Si ya venció regresa
 * "ya venció" (sin "vence" para evitar mezclar tiempos verbales).
 *
 *   "23 min"
 *   "2h 15min"
 *   "1 día"
 */
export function formatTimeRemainingShort(slaDueAt: string | Date): string {
  const ms = msUntilDue(slaDueAt);
  if (ms <= 0) return 'ya venció';
  return formatRemainingDuration(ms);
}

function msUntilDue(slaDueAt: string | Date): number {
  const due = typeof slaDueAt === 'string' ? new Date(slaDueAt) : slaDueAt;
  return due.getTime() - Date.now();
}

function formatRemainingDuration(ms: number): string {
  const hours = Math.floor(ms / HOUR);
  const minutes = Math.floor((ms % HOUR) / (60 * 1000));
  if (hours === 0) return `${minutes} min`;
  if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 día' : `${days} días`;
}

/**
 * Genera el mensaje de acuse automático que se publica como primera
 * respuesta del equipo cuando se crea un ticket P3 o P4. Confirma al
 * cliente que su reporte llegó y le dice cuándo esperar seguimiento.
 *
 * Para P1/P2 NO mandamos acuse automático — vamos a responder
 * personalmente y rápido, un mensaje robotizado en medio se siente frío.
 */
export function getAutoAckMessage(priority: TicketPriority): string | null {
  if (priority === 'P1' || priority === 'P2') return null;

  const sla = ACTIVE_SLA[priority];
  if (priority === 'P3') {
    return `¡Hola! Recibimos tu reporte y ya está en nuestra cola.\n\nLo evaluamos y te respondemos dentro de **${sla.label}**. Mientras tanto, tu app sigue funcionando normal.\n\nGracias por tomarte el tiempo de reportarlo — nos ayuda a mejorar.\n\n— Insane Force Team Support`;
  }
  // P4
  return `¡Hola! Recibimos tu mensaje. ✅\n\nLo revisamos y te respondemos dentro de los próximos **${sla.label}**. Tu app sigue funcionando normal mientras tanto.\n\nGracias por compartirnos esto — cada reporte nos ayuda a hacer mejor la app.\n\n— Insane Force Team Support`;
}

/**
 * Helper para mostrar al cliente al crear el ticket cuál es el SLA
 * que esperar.
 *
 *   P1 → "Te respondemos en máximo 1 hora"
 *   P2 → "Te respondemos en máximo 4 horas"
 *   P3 → "Te respondemos en máximo 24 horas"
 *   P4 → "Te respondemos en máximo 3 días"
 */
export function getSlaPromiseToUser(priority: TicketPriority): string {
  const sla = ACTIVE_SLA[priority];
  return `Te respondemos en máximo ${sla.label}`;
}
